import { Check } from 'lucide-react';
import {
  BUG_PRIORITIES,
  BUG_STATUSES,
  type BugReport,
  type BugReportPriority,
  type BugReportStatus,
} from '../../lib/bugReportsApi';
import { useUpdateBugReport } from '../../hooks/useBugReports';
import { Popover } from '../tasks/linear/Popover';
import { PriorityIcon, StatusIcon } from '../tasks/linear/taskVisuals';
import {
  BUG_PRIORITY_AS_TASK,
  BUG_PRIORITY_COLOR,
  BUG_STATUS_CATEGORY,
  BUG_STATUS_COLOR,
  useBugLabels,
} from './bugVisuals';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Status and priority pickers for the bug detail sidebar.
 *
 * Deliberately the same control as a task's: an icon trigger that opens a
 * popover of options with a tick on the current one, not a form `<select>`.
 * Triaging a bug is the same gesture as moving a ticket, and the two pages sit
 * next to each other in the sidebar — a different widget on each would read as
 * two different products.
 */

export function BugStatusEditor({ report }: { report: BugReport }) {
  const { t } = useI18n();
  const labels = useBugLabels();
  const update = useUpdateBugReport(report.id);

  function pick(status: BugReportStatus, close: () => void) {
    close();
    if (status === report.status) return;
    update.mutate({ status });
  }

  return (
    <Popover
      width={210}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200"
          title={`${t('bugReports.column.status')}: ${labels.status(report.status)}`}
          aria-label={t('bugReports.changeStatus')}
        >
          <StatusIcon
            category={BUG_STATUS_CATEGORY[report.status]}
            color={BUG_STATUS_COLOR[report.status]}
            size={14}
          />
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {BUG_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => pick(status, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <StatusIcon
                  category={BUG_STATUS_CATEGORY[status]}
                  color={BUG_STATUS_COLOR[status]}
                  size={14}
                />
                <span style={{ color: BUG_STATUS_COLOR[status] }}>{labels.status(status)}</span>
              </span>
              {status === report.status && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

export function BugPriorityEditor({ report }: { report: BugReport }) {
  const { t } = useI18n();
  const labels = useBugLabels();
  const update = useUpdateBugReport(report.id);

  function pick(priority: BugReportPriority, close: () => void) {
    close();
    if (priority === report.priority) return;
    update.mutate({ priority });
  }

  return (
    <Popover
      width={210}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="flex h-5 w-5 items-center justify-center rounded hover:bg-gray-200"
          title={`${t('bugReports.column.priority')}: ${labels.priority(report.priority)}`}
          aria-label={t('bugReports.changePriority')}
        >
          <PriorityIcon priority={BUG_PRIORITY_AS_TASK[report.priority]} size={14} />
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {/* Most urgent first, like the task menu. */}
          {[...BUG_PRIORITIES].reverse().map((priority) => (
            <button
              key={priority}
              type="button"
              onClick={() => pick(priority, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <PriorityIcon priority={BUG_PRIORITY_AS_TASK[priority]} size={14} />
                <span style={{ color: BUG_PRIORITY_COLOR[priority] }}>
                  {labels.priority(priority)}
                </span>
              </span>
              {priority === report.priority && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
